import { useEffect, useState } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Lightbox } from './Lightbox';
import type { LightboxCaption, LightboxImageSize } from './Lightbox';

const vanDykeImages: [string, string, string] = [
  'https://assets.salieflewis.com/slide_one.png',
  'https://assets.salieflewis.com/slide_two.png',
  'https://assets.salieflewis.com/slide_three.png',
];

const vanDykeCaptions: [LightboxCaption, LightboxCaption, LightboxCaption] = [
  {
    title: 'Untitled (Milk Crate)',
    year: '2026',
  },
  {
    title: 'Dancing Feet',
    year: '2026',
  },
  {
    title: 'Untitled (Painted Plank)',
    year: '2026',
  },
];

const vanDykeImageSizes: [
  LightboxImageSize,
  LightboxImageSize,
  LightboxImageSize,
] = [
  {
    width: 1080,
    height: 1350,
  },
  {
    width: 1080,
    height: 1350,
  },
  {
    width: 1080,
    height: 1350,
  },
];

function preloadImages(urls: string[]) {
  for (const url of urls) {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  }
}

export function VanDyke() {
  const [show, setShow] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setShow(false);
    const timer = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const warmImages = () => preloadImages(vanDykeImages);

    if ('requestIdleCallback' in window) {
      const idleCallback = window.requestIdleCallback(warmImages);
      return () => window.cancelIdleCallback(idleCallback);
    }

    const timer = setTimeout(warmImages, 600);
    return () => clearTimeout(timer);
  }, []);

  const imageTrail = useTrail(vanDykeImages.length, {
    from: { opacity: 0 },
    to: show ? { opacity: 1 } : { opacity: 0 },
    config: { duration: 250 },
  });

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setCurrentImageIndex(0);
  };

  return (
    <>
      <div className="relative z-1 flex w-full items-center justify-center bg-transparent p-0">
        <div className="flex max-w-full flex-row items-center justify-center gap-3 p-4 md:gap-8">
          {vanDykeImages.map((image, index) => (
            <animated.div
              key={image}
              className={`relative z-1 w-[min(82vw,640px)] shrink-0 origin-center cursor-pointer transition-transform duration-500 ease-out md:block md:w-[min(27vw,360px)] md:hover:scale-[1.02] ${
                index === 0 ? '' : 'hidden'
              }`}
              style={imageTrail[index]}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={image}
                alt={vanDykeCaptions[index].title}
                width={vanDykeImageSizes[index].width}
                height={vanDykeImageSizes[index].height}
                loading="eager"
                decoding="async"
                fetchPriority={index === 0 ? 'high' : 'auto'}
                className="block h-auto w-full object-contain md:max-h-[min(58vh,680px)] xl:max-h-[720px]"
              />
            </animated.div>
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox
          images={vanDykeImages}
          caption={vanDykeCaptions[0]}
          captions={vanDykeCaptions}
          imageSizes={vanDykeImageSizes}
          currentIndex={currentImageIndex}
          onClose={handleCloseLightbox}
          onNavigate={setCurrentImageIndex}
        />
      )}
    </>
  );
}
